import { format, formatDuration, intervalToDuration } from 'date-fns';
import { ArrowLeft, Bug, Code, FileJson, Users } from 'lucide-react';
import { buildBitString } from '../bitEncoding';
import { FLOW } from '../constants';
import { useStore } from '../store';
import { SecPoseReannotationPanel } from './SecPoseReannotationPanel';

export function EpisodeDebug() {
  const { episodeDebugData, debugEpisodeId, showEpisodesManagement, isLoading } = useStore();

  const handleBack = () => {
    void showEpisodesManagement();
  };

  const votes = Array.isArray(episodeDebugData?.episode?.secshot_votes)
    ? episodeDebugData.episode.secshot_votes
    : [];
  const auditVote = episodeDebugData?.episode?.secshot_audit ?? null;
  // secPose episodes record operations in pose_annotations, not votes/audits.
  // When present, show that section and hide the bitmask "Operator Votes" block.
  const poseAnnotations = Array.isArray(episodeDebugData?.pose_annotations)
    ? episodeDebugData.pose_annotations
    : [];
  const isPoseFlow = poseAnnotations.length > 0;
  const debugLength =
    typeof episodeDebugData?.episode?.duration === 'number' &&
    Number.isFinite(episodeDebugData.episode.duration)
      ? episodeDebugData.episode.duration + 1
      : null;

  function fmtDate(ms: number | null | undefined): string | null {
    if (ms == null) return null;
    return format(new Date(ms), 'MMMM d, yyyy, h:mm:ss a');
  }
  function fmtDuration(ms: number | null | undefined): string | null {
    if (ms == null) return null;
    return formatDuration(intervalToDuration({ start: 0, end: ms }));
  }

  // Transform raw data for readable display in JSON block
  function prettifyDebugData(data: typeof episodeDebugData) {
    if (!data) return data;
    const clone = JSON.parse(JSON.stringify(data)) as typeof data;
    const transformVote = (v: unknown) => {
      const r = v as Record<string, unknown>;
      if (typeof r.completed_at_ms === 'number') {
        r.completed_at = fmtDate(r.completed_at_ms);
        delete r.completed_at_ms;
      }
      if (typeof r.completion_duration_ms === 'number') {
        r.completion_duration_s = Math.round(r.completion_duration_ms / 1000);
        delete r.completion_duration_ms;
      }
    };
    if (Array.isArray(clone?.episode?.secshot_votes)) {
      for (const v of clone.episode.secshot_votes as unknown[]) transformVote(v);
    }
    if (clone?.episode?.secshot_audit) {
      transformVote(clone.episode.secshot_audit);
    }
    const ep = clone?.episode as unknown as Record<string, unknown> | undefined;
    if (ep && typeof ep.duration === 'number') {
      ep.video_length_s = ep.duration;
      delete ep.duration;
    }
    return clone;
  }

  // Episode-level summary
  const episodeEndTime = auditVote?.completed_at_ms ?? null;
  const allDurations = [
    ...votes.map((v) => v.completion_duration_ms),
    auditVote?.completion_duration_ms,
  ].filter((v): v is number => v != null);
  const episodeTotalDurationMs =
    allDurations.length > 0 ? allDurations.reduce((a, b) => a + b, 0) : null;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <Bug className="h-8 w-8 text-brand-orange" />
            Debug Console
          </h2>
          <div className="flex items-center gap-2 text-sm text-brand-text-secondary mt-2 font-mono">
            <span>ID:</span>
            <span className="text-brand-text-primary">{debugEpisodeId}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleBack}
          className="self-start sm:self-center flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-4 py-2 text-sm font-medium text-brand-text-secondary transition hover:border-brand-text-secondary/50 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to List
        </button>
      </div>

      <div className="flex flex-col gap-8">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-brand-text-primary flex items-center gap-2">
            <FileJson className="h-5 w-5 text-brand-text-secondary" />
            Raw Data
          </h3>
          <div className="rounded-xl border border-brand-border bg-brand-surface p-1 shadow-sm overflow-hidden">
            <pre className="w-full max-h-[600px] overflow-auto rounded-lg bg-brand-dark p-4 text-xs font-mono text-brand-text-secondary">
              {isLoading
                ? 'Loading...'
                : episodeDebugData
                  ? JSON.stringify(prettifyDebugData(episodeDebugData), null, 2)
                  : 'No data'}
            </pre>
          </div>
        </div>

        {FLOW === 'secpose' && debugEpisodeId && (
          <SecPoseReannotationPanel episodeId={debugEpisodeId} />
        )}

        {isPoseFlow && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-brand-text-primary flex items-center gap-2">
              <Users className="h-5 w-5 text-brand-text-secondary" />
              Pose Annotations
            </h3>
            <div className="flex flex-col gap-4">
              {poseAnnotations.map((pa) => {
                const isAudit = pa.kind === 'audit';
                const completionDate = fmtDate(pa.created_at_ms);
                const completionDuration = fmtDuration(pa.completion_duration_ms);
                return (
                  <div
                    key={`${pa.operator}:${pa.kind}`}
                    className={`rounded-xl border bg-brand-surface p-5 ${
                      isAudit ? 'border-brand-orange/30' : 'border-brand-border'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-8 w-8 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-xs text-brand-text-secondary font-bold">
                        {pa.operator.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-brand-text-primary">
                          {pa.operator}{' '}
                          <span
                            className={`ml-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                              isAudit
                                ? 'bg-brand-orange/20 text-brand-orange'
                                : 'bg-brand-dark text-brand-text-secondary'
                            }`}
                          >
                            {pa.kind}
                          </span>
                        </div>
                        <div className="text-xs text-brand-text-secondary mt-1 space-y-0.5">
                          <div>Frames: {pa.frame_count ?? '—'}</div>
                          {completionDate != null && <div>Completion Date: {completionDate}</div>}
                          {completionDuration != null && (
                            <div>Completion Duration: {completionDuration}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                        R2 Result Keys
                      </div>
                      <div className="rounded bg-brand-dark px-3 py-2 text-xs font-mono text-brand-orange break-all">
                        {pa.written_keys && pa.written_keys.length > 0
                          ? pa.written_keys.join('\n')
                          : '(none)'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={`space-y-4 ${isPoseFlow ? 'hidden' : ''}`}>
          <h3 className="text-lg font-semibold text-brand-text-primary flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-text-secondary" />
            Operator Votes
          </h3>

          {episodeDebugData && (
            <div className="flex flex-col gap-4">
              {votes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface p-8 text-center text-sm text-brand-text-secondary">
                  No votes recorded yet.
                </div>
              ) : (
                votes.map((vote) => {
                  const encodedBits = buildBitString(vote.encoded, debugLength);
                  const encodedDisplay =
                    encodedBits === null ? '(invalid)' : encodedBits || '(empty)';
                  const completionDate = fmtDate(vote.completed_at_ms);
                  const completionDuration = fmtDuration(vote.completion_duration_ms);
                  return (
                    <div
                      key={vote.operator}
                      className="group rounded-xl border border-brand-border bg-brand-surface p-5 transition hover:border-brand-orange/30"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-xs text-brand-text-secondary font-bold">
                            {vote.operator.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-brand-text-primary">
                              {vote.operator}
                            </div>
                            <div className="text-xs text-brand-text-secondary">
                              {debugLength ?? '—'} seconds
                            </div>
                            {(completionDate != null || completionDuration != null) && (
                              <div className="text-xs text-brand-text-secondary mt-1 space-y-0.5">
                                {completionDate != null && (
                                  <div>Completion Date: {completionDate}</div>
                                )}
                                {completionDuration != null && (
                                  <div>Completion Duration: {completionDuration}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                            Encoded String
                          </div>
                          <div className="rounded bg-brand-dark px-3 py-2 text-xs font-mono text-brand-orange break-all">
                            {vote.encoded || '(empty)'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                            Bit Array
                          </div>
                          <div className="rounded bg-brand-dark px-3 py-2 text-xs font-mono text-brand-text-primary break-all tracking-widest">
                            {encodedDisplay}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                            Accuracy vs Audit
                          </div>
                          <div className="rounded bg-brand-dark px-3 py-2 text-xs font-mono text-brand-text-primary">
                            {(() => {
                              const m = vote.audit_match_bits;
                              const t = vote.audit_total_bits;
                              if (m == null || t == null) {
                                return (
                                  <span className="text-brand-text-secondary">
                                    — (no audit yet)
                                  </span>
                                );
                              }
                              if (t === 0) return <span>—</span>;
                              const pct = (m / t) * 100;
                              return (
                                <span>
                                  <span className="text-brand-orange font-bold">
                                    {pct.toFixed(1)}%
                                  </span>
                                  <span className="text-brand-text-secondary ml-2">
                                    ({m}/{t} bits)
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {auditVote && (
                <div className="rounded-xl border border-brand-orange/30 bg-brand-surface p-5 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-brand-orange"></div>

                  <h4 className="text-sm font-bold text-brand-orange uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Code className="h-4 w-4" /> Audited Result
                  </h4>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="h-8 w-8 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-xs text-brand-text-secondary font-bold">
                        {(auditVote.operator ?? '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-brand-text-primary">
                          {auditVote.operator ?? '—'}{' '}
                          <span className="text-brand-text-secondary font-normal">(auditor)</span>
                        </div>
                        {(fmtDate(auditVote.completed_at_ms) != null ||
                          fmtDuration(auditVote.completion_duration_ms) != null) && (
                          <div className="text-xs text-brand-text-secondary mt-0.5 space-y-0.5">
                            {fmtDate(auditVote.completed_at_ms) != null && (
                              <div>Completion Date: {fmtDate(auditVote.completed_at_ms)}</div>
                            )}
                            {fmtDuration(auditVote.completion_duration_ms) != null && (
                              <div>
                                Completion Duration: {fmtDuration(auditVote.completion_duration_ms)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                        Final String
                      </div>
                      <div className="text-xs font-mono text-white break-all">
                        {auditVote.encoded}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-1">
                        Final Bit Array
                      </div>
                      <div className="rounded bg-brand-dark px-3 py-2 text-xs font-mono text-brand-orange break-all tracking-widest border border-brand-border">
                        {(() => {
                          const bits = buildBitString(auditVote.encoded, debugLength);
                          return bits === null ? '(invalid)' : bits || '(empty)';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Episode-level summary */}
              {(episodeEndTime != null || episodeTotalDurationMs != null) && (
                <div className="rounded-xl border border-brand-border bg-brand-surface px-5 py-4 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-brand-text-secondary mb-2">
                    Episode Summary
                  </div>
                  {episodeEndTime != null && (
                    <div className="text-xs text-brand-text-secondary">
                      Completion Date:{' '}
                      <span className="text-brand-text-primary">{fmtDate(episodeEndTime)}</span>
                    </div>
                  )}
                  {episodeTotalDurationMs != null && (
                    <div className="text-xs text-brand-text-secondary">
                      Total Duration (all operators):{' '}
                      <span className="text-brand-text-primary">
                        {fmtDuration(episodeTotalDurationMs)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
